import * as THREE from 'three';

/** World units per map cell (metres). */
export const CELL = 8;
/** How far block walls / fences sit inside a street cell, from the cell edge. */
export const FENCE_INSET = 1.3;
/** Characters keep this far from cell edges that border buildings. */
export const WALK_MARGIN = 1.65;

/*
 * Map legend (levels are ASCII grids, see src/levels/)
 *  #  building / garden        .  street (dark)
 *  L  streetlight              F  streetlight that flickers and dies as she nears
 *  V  vending machines (light) G  torii gate (start of a quiet path)
 *  p  quiet path (shrine / park): dark, and he won't follow her in
 *  T  motion-sensor floodlight B  broken light
 *  X  road works barrier       C  konbini (building, bright front)
 *  A  a lit apartment entrance that isn't hers   H  her apartment entrance (goal)
 *  S  start                    P  where he appears   E  station building
 *  R  covered arcade           Y  arcade + ceiling lamp   W  arcade lamp that dies
 *  Q  arcade + open shop (its shutter rolls down as she passes)
 *  g  park lawn                n  lawn with a bench  b  bridge   z  stone steps
 *  w  water (blocked, but you can see across it)   t  trees   h  hedge (blocked)
 *  j  bamboo grove (blocked)
 */
const STREET_CHARS = new Set(['.', 'L', 'F', 'V', 'G', 'T', 'B', 'S', 'P', 'A', 'H', 'X', 'p', 'R', 'Y', 'W', 'Q', 'g', 'n', 'b', 'z', 'K', 'u', 'U', 'k']);
/** Blocked cells that don't block the view. */
const SEE_THROUGH = new Set(['w', 'r']);
/** Path cells: dark, quiet, and he will not follow her into them. */
const PATH_CHARS = new Set(['p', 'G']);

export type CellRef = { c: number; r: number };

export class CityMap {
  readonly width: number;
  readonly height: number;
  /** Small round obstacles inside street cells (benches). */
  private readonly blockers: { x: number; z: number; r: number }[] = [];
  /** Level-crossing gates ('K' cells) are down: nobody can cross. */
  gatesClosed = false;
  /** Cells on a branch that only leads to a dead end (pruned leaves of the street graph). */
  private readonly deadEnd: Uint8Array;

  constructor(private readonly rows: readonly string[]) {
    this.width = rows[0].length;
    this.height = rows.length;
    for (const row of rows) {
      if (row.length !== this.width) throw new Error('Map rows must have equal width.');
    }
    this.deadEnd = this.computeDeadEnds();
  }

  /** The raw rows (sent to the phone so its map app can draw this level). */
  get layout(): readonly string[] {
    return this.rows;
  }

  isDeadEnd(c: number, r: number): boolean {
    return this.inBounds(c, r) && this.deadEnd[this.index(c, r)] === 1;
  }

  private computeDeadEnds(): Uint8Array {
    const removed = new Uint8Array(this.width * this.height);
    const degree = (c: number, r: number) => {
      let n = 0;
      for (const [dc, dr] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nc = c + dc;
        const nr = r + dr;
        if (this.isPassable(nc, nr) && !removed[this.index(nc, nr)]) n += 1;
      }
      return n;
    };
    let changed = true;
    while (changed) {
      changed = false;
      for (let r = 0; r < this.height; r += 1) {
        for (let c = 0; c < this.width; c += 1) {
          const i = this.index(c, r);
          if (removed[i] || !this.isPassable(c, r)) continue;
          if (degree(c, r) <= 1) {
            removed[i] = 1;
            changed = true;
          }
        }
      }
    }
    return removed;
  }

  index(c: number, r: number): number {
    return r * this.width + c;
  }

  inBounds(c: number, r: number): boolean {
    return c >= 0 && r >= 0 && c < this.width && r < this.height;
  }

  char(c: number, r: number): string {
    if (!this.inBounds(c, r)) return '#';
    return this.rows[r][c];
  }

  /** Paved / walkable cell (includes the road-works cell, which is partly blocked). */
  isStreet(c: number, r: number): boolean {
    return STREET_CHARS.has(this.char(c, r));
  }

  /** Cell that path-finding may pass through. */
  isPassable(c: number, r: number): boolean {
    return this.isStreet(c, r) && this.char(c, r) !== 'X';
  }

  addBlocker(x: number, z: number, radius: number): void {
    this.blockers.push({ x, z, r: radius });
  }

  /** A quiet path cell (shrine / park path). */
  isShrine(c: number, r: number): boolean {
    return PATH_CHARS.has(this.char(c, r));
  }

  cellAt(x: number, z: number): CellRef {
    return { c: Math.floor(x / CELL), r: Math.floor(z / CELL) };
  }

  center(c: number, r: number, out = new THREE.Vector3()): THREE.Vector3 {
    return out.set((c + 0.5) * CELL, 0, (r + 0.5) * CELL);
  }

  find(ch: string): CellRef[] {
    const found: CellRef[] = [];
    for (let r = 0; r < this.height; r += 1) {
      for (let c = 0; c < this.width; c += 1) {
        if (this.rows[r][c] === ch) found.push({ c, r });
      }
    }
    return found;
  }

  /** Number of open orthogonal neighbours. */
  openNeighbours(c: number, r: number): number {
    let n = 0;
    if (this.isPassable(c - 1, r)) n += 1;
    if (this.isPassable(c + 1, r)) n += 1;
    if (this.isPassable(c, r - 1)) n += 1;
    if (this.isPassable(c, r + 1)) n += 1;
    return n;
  }

  /**
   * Point-in-walkable-area test. Street cells are inset by `margin` from any
   * edge or corner that touches a building cell; the road-works cell is only
   * walkable on its southern half.
   */
  isWalkable(x: number, z: number, margin = WALK_MARGIN): boolean {
    const c = Math.floor(x / CELL);
    const r = Math.floor(z / CELL);
    if (!this.isStreet(c, r)) return false;
    const lx = x - c * CELL;
    const lz = z - r * CELL;
    if (this.char(c, r) === 'X' && lz < CELL * 0.56) return false;
    if (this.gatesClosed && this.char(c, r) === 'K') return false;

    const lo = lx < margin;
    const hi = lx > CELL - margin;
    const ln = lz < margin;
    const ls = lz > CELL - margin;
    if (lo && !this.isStreet(c - 1, r)) return false;
    if (hi && !this.isStreet(c + 1, r)) return false;
    if (ln && !this.isStreet(c, r - 1)) return false;
    if (ls && !this.isStreet(c, r + 1)) return false;
    if (lo && ln && !this.isStreet(c - 1, r - 1)) return false;
    if (hi && ln && !this.isStreet(c + 1, r - 1)) return false;
    if (lo && ls && !this.isStreet(c - 1, r + 1)) return false;
    if (hi && ls && !this.isStreet(c + 1, r + 1)) return false;
    for (const b of this.blockers) {
      if ((x - b.x) ** 2 + (z - b.z) ** 2 < b.r * b.r) return false;
    }
    return true;
  }

  /** True when the straight segment between two points stays over street cells. */
  lineOfSight(ax: number, az: number, bx: number, bz: number, step = 0.7): boolean {
    const dx = bx - ax;
    const dz = bz - az;
    const len = Math.hypot(dx, dz);
    const n = Math.max(1, Math.ceil(len / step));
    for (let i = 1; i < n; i += 1) {
      const t = i / n;
      const x = ax + dx * t;
      const z = az + dz * t;
      const c = Math.floor(x / CELL);
      const r = Math.floor(z / CELL);
      if (!this.isStreet(c, r) && !SEE_THROUGH.has(this.char(c, r))) return false;
    }
    return true;
  }

  /** Breadth-first path distance (in cells) from one cell to every passable cell; -1 = unreachable. */
  distanceField(fromC: number, fromR: number, out?: Int16Array): Int16Array {
    const field = out ?? new Int16Array(this.width * this.height);
    field.fill(-1);
    if (!this.isPassable(fromC, fromR)) return field;
    const queue = new Int32Array(this.width * this.height);
    let head = 0;
    let tail = 0;
    const start = this.index(fromC, fromR);
    field[start] = 0;
    queue[tail++] = start;
    while (head < tail) {
      const i = queue[head++];
      const c = i % this.width;
      const r = (i - c) / this.width;
      const d = field[i] + 1;
      if (this.isPassable(c - 1, r) && field[i - 1] < 0) { field[i - 1] = d; queue[tail++] = i - 1; }
      if (this.isPassable(c + 1, r) && field[i + 1] < 0) { field[i + 1] = d; queue[tail++] = i + 1; }
      if (this.isPassable(c, r - 1) && field[i - this.width] < 0) { field[i - this.width] = d; queue[tail++] = i - this.width; }
      if (this.isPassable(c, r + 1) && field[i + this.width] < 0) { field[i + this.width] = d; queue[tail++] = i + this.width; }
    }
    return field;
  }

}
